// JobQuestionnaire — Pre-pipeline questionnaire tab on the Job Detail page.
// HR builds the questionnaire here and bulk-approves shortlisted candidates.
// Questionnaire is sent after HR approval; pipeline only unlocks after submission.

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Send, CheckCircle, Loader2, Clock, Users } from "lucide-react";
import { toast } from "react-toastify";
import api from "../../services/api";
import QuestionnaireBuilder from "../../components/questionnaire/QuestionnaireBuilder";

export default function JobQuestionnaire() {
  const { id: jobId } = useParams();

  const [shortlistedCount, setShortlistedCount] = useState(null);
  const [showModal, setShowModal]               = useState(false);
  const [deadline, setDeadline]                 = useState("");
  // Approving and sending are two steps (30 Oct 2026): empty = send now.
  const [sendOn, setSendOn]                     = useState("");
  const [sending, setSending]                   = useState(false);

  useEffect(() => {
    api.get(`/resumes/job/${jobId}?filter=shortlisted&limit=1`)
      .then(r => setShortlistedCount(r.data?.pagination?.total ?? r.data?.data?.length ?? "?"))
      .catch(() => setShortlistedCount("?"));
  }, [jobId]);

  const handleBulkApprove = async () => {
    if (!deadline) { toast.warn("Please set a deadline first."); return; }
    setSending(true);
    try {
      const r = await api.post(`/questionnaire/${jobId}/bulk-approve`, { deadline, ...(sendOn ? { sendOn } : {}) });
      if (r.data.scheduledFor) {
        toast.success(`${r.data.approved} candidate(s) approved. The questionnaire goes to them on ${new Date(r.data.scheduledFor).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}.`);
      } else {
        toast.success(`Questionnaire sent to ${r.data.sent} candidate(s).${r.data.held ? ` ${r.data.message}` : ""}`);
      }
      setShowModal(false);
      setDeadline("");
      setSendOn("");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to send questionnaire.");
    } finally {
      setSending(false);
    }
  };

  // Min datetime for deadline picker — now + 1 hour
  const minDeadline = new Date(Date.now() + 60 * 60 * 1000).toISOString().slice(0, 16);

  return (
    <div className="space-y-6">
      {/* Header bar */}
      <div className="flex items-center justify-between bg-white border rounded-2xl shadow-sm px-5 py-4">
        <div>
          <h2 className="font-bold text-gray-900 text-base">Pre-Pipeline Questionnaire</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Build the questionnaire below, then send it to all shortlisted candidates at once.
            The pipeline stages unlock only after a candidate submits this questionnaire.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition whitespace-nowrap"
        >
          <Send className="w-4 h-4" />
          Bulk Approve &amp; Send
          {shortlistedCount !== null && (
            <span className="bg-blue-500 text-white text-xs px-1.5 py-0.5 rounded-full">
              {shortlistedCount}
            </span>
          )}
        </button>
      </div>

      {/* Questionnaire builder */}
      <QuestionnaireBuilder jobId={jobId} stageId="pre-pipeline" stageLabel="Pre-Pipeline Questionnaire" />

      {/* Bulk Approve Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Bulk Approve &amp; Send</h3>
                <p className="text-xs text-gray-500">
                  Questionnaire will be emailed to all shortlisted candidates.
                </p>
              </div>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Clock className="w-4 h-4 inline mr-1 text-gray-400" />
                Submission Deadline
              </label>
              <input
                type="datetime-local"
                min={minDeadline}
                value={deadline}
                onChange={e => setDeadline(e.target.value)}
                className="w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <p className="text-xs text-gray-400 mt-1.5">
                Candidates who miss this deadline will be blocked from pipeline stages unless manually approved by HR.
              </p>
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                <Send className="w-4 h-4 inline mr-1 text-gray-400" />
                Send on (optional)
              </label>
              <input
                type="datetime-local"
                min={new Date().toISOString().slice(0, 16)}
                value={sendOn}
                onChange={e => setSendOn(e.target.value)}
                aria-label="Send on"
                className="w-full border rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <p className="text-xs text-gray-400 mt-1.5">
                Leave empty to send now. With a date, the candidates are approved now and the questionnaire goes to them on that day.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-medium py-2.5 rounded-xl hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkApprove}
                disabled={sending || !deadline}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {sending ? "Working…" : sendOn ? "Approve now, send on that day" : "Send to All Shortlisted"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
